import { ExpandableTable } from "../../../../components/UniversalTable";
import { documentTypeTableColumns } from "../../../../helpers";
import { resolveDocumentUrl } from "../../../../utils/fraudRisk";
import { BankStatementItem } from "../../../../types/verified/bank-statment";
import { useMemo } from "react";
import { ExpandedTableRow } from "./slices/ExpandedTableRow";
import { IncomeStatus, VerifyMethodType } from "shared/types";
import { VerificationSpecificAdditionaData } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { BankStatementTableRowData } from "./types";

const sortBankStatementRows = (
  a: BankStatementTableRowData,
  b: BankStatementTableRowData
) => {
  const dateA =
    a.additional_data?.bank_statement_start_date ||
    a.additional_data?.earliest_transaction_date;
  const dateB =
    b.additional_data?.bank_statement_start_date ||
    b.additional_data?.earliest_transaction_date;

  if (!dateA) return 1;
  if (!dateB) return -1;

  return new Date(dateB).getTime() - new Date(dateA).getTime();
};

export const BankStatementTable = ({
  incomeItems,
  incomeStatus,
  additionalData
}: {
  incomeItems?: BankStatementItem[];
  incomeStatus: IncomeStatus;
  additionalData?: VerificationSpecificAdditionaData[VerifyMethodType.BankStatement];
}) => {
  const mapBankStatementToRow = (
    incomeSourceId: number
  ): BankStatementTableRowData => {
    const bankStatement = additionalData?.allBankStatements?.[incomeSourceId];
    return {
      ...bankStatement,
      usedItems: incomeItems?.filter(
        (item) => item.income_source_id === incomeSourceId
      ),
      verifyType: VerifyMethodType.BankStatement,
      companyName: additionalData?.companyName,
      incomeId: additionalData?.incomeId,
      incomeStatus: incomeStatus,
      name_mismatch: bankStatement?.additional_data?.name_mismatch
    };
  };

  const bankStatements = useMemo(() => {
    // Used bank statements
    const usedIncomeSourceIds = new Set(
      incomeItems?.map((item) => item.income_source_id)
    );
    const usedBankStatements = Array.from(usedIncomeSourceIds)
      .map((incomeSourceId) => ({
        ...mapBankStatementToRow(incomeSourceId),
        hasTransactions: true
      }))
      .sort(sortBankStatementRows);

    // Unused bank statements
    const allIncomeSourceIds = Object.keys(
      additionalData?.allBankStatements ?? {}
    ).map((id) => +id);
    const unusedIncomeSourceIds = allIncomeSourceIds.filter(
      (incomeSourceId) => !usedIncomeSourceIds.has(incomeSourceId)
    );
    const unusedBankStatements = unusedIncomeSourceIds
      .map((incomeSourceId) => ({
        ...mapBankStatementToRow(incomeSourceId),
        hasTransactions: false
      }))
      .filter(
        (bankStatementRow) =>
          bankStatementRow.additional_data?.type_mismatch !== true
      )
      .sort(sortBankStatementRows);

    return [...usedBankStatements, ...unusedBankStatements];
  }, [incomeItems, additionalData]);

  return (
    <ExpandableTable
      id={`income-table-${additionalData?.incomeId}`}
      cellClassName="p-4 pl-0 text-sm self-center"
      headerRowClassName="text-content-300 !font-medium"
      renderExpanded={(row) => {
        return (
          <div className="p-6 bg-background-900">
            <ExpandedTableRow rowData={row} />
          </div>
        );
      }}
      rows={bankStatements}
      columns={documentTypeTableColumns}
      getViewUrl={resolveDocumentUrl}
    />
  );
};
